import { say } from '@/lib/vocabulary';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { backend } from '@/api/backend';
import { MAX_PAGES, useWholeGraphs } from './useWholeGraph';
import { EntityPreview } from '@/components/EntityPreview';
import { ApiError } from '@/api/http';

import { EmptyState, Stamp, Loading } from '@/components/ui';
import { tidyForest } from '@/pages/treeLayout';
import {
  type Camera,
  fit as fitCamera,
  panBy,
  screenToWorld,
  wheelFactor,
  zoomAt,
} from '@/pages/camera';
import { bundledIcon, iconDataUri, faviconServiceUrl } from '@/lib/toolicons';
import { WalkProgress, type WalkState } from '@/components/WalkProgress';
import { every, walkOutstanding } from '@/pages/livePolling';
import { useCallback } from 'react';
import { FAMILIES_REQUIRED } from '@/components/PromoteDetection';

/**
 * The shape of a column, in world units.
 *
 * A label sits to the RIGHT of its node here rather than centred underneath
 * it. Centred labels are what a force layout needs, because a node can have a
 * neighbour in any direction; in a layered tree the only neighbour that can
 * collide is the one in the next row, so a left-aligned label beside the dot
 * reads as an outline and never overlaps the column beside it.
 *
 * `COLUMN_GAP` is therefore the widest label plus the dot, and `ROW_GAP` is
 * the two-line label block plus air.
 */
const LABEL_MAX = 190;
const COLUMN_GAP = LABEL_MAX + 96;
const ROW_GAP = 40;
const TIDY = { columnGap: COLUMN_GAP, rowGap: ROW_GAP, rootGap: 1 };
/** Rows a band holds before the next root wraps into a column to its right. */
const bandRows = (height: number) => Math.max(6, Math.floor((height || 480) / ROW_GAP) - 1);
/** Room for the label that overhangs the last column when fitting. */
const FIT_PADDING = 72;

/** Nodes and edges drawn at once. Named, stated on screen, never silent. */
const RENDER_NODE_CAP = 300;

interface DomainShape {
  signatureSha256: string;
  recordShape: string;
  signatureKeys: string[];
  objectCount: number;
  toolId: string | null;
}
interface DomainAgreement {
  source: string;
  target: string;
  sharedKeys: string[];
  weight: number;
}

interface DomainGroup {
  domainKey: string;
  joiningKeys: string[];
  toolIds: string[];
  signatureSha256s: string[];
  shapeCount: number;
  objectCount: number;
}

/** The backend's domain graph, or null when it has nothing to draw. */
function readDomainGraph(
  value: unknown,
): { nodes: DomainShape[]; edges: DomainAgreement[]; groups: DomainGroup[] } | null {
  const data = (value as { data?: unknown })?.data ?? value;
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (!Array.isArray(d.nodes)) return null;
  const num = (v: unknown) => (typeof v === 'number' ? v : 0);
  const arr = (v: unknown) => (Array.isArray(v) ? (v as string[]) : []);
  return {
    groups: (Array.isArray(d.domains) ? (d.domains as Record<string, unknown>[]) : []).map((g) => ({
      domainKey: String(g.domainKey ?? ''),
      joiningKeys: arr(g.joiningKeys),
      toolIds: arr(g.toolIds),
      signatureSha256s: arr(g.signatureSha256s),
      shapeCount: num(g.shapeCount),
      objectCount: num(g.objectCount),
    })),
    nodes: (d.nodes as Record<string, unknown>[]).map((n) => ({
      signatureSha256: String(n.signatureSha256 ?? ''),
      recordShape: String(n.recordShape ?? ''),
      signatureKeys: arr(n.signatureKeys),
      objectCount: num(n.objectCount),
      toolId: typeof n.toolId === 'string' ? n.toolId : null,
    })),
    edges: (Array.isArray(d.edges) ? (d.edges as Record<string, unknown>[]) : []).map((e) => ({
      source: String(e.source ?? ''),
      target: String(e.target ?? ''),
      sharedKeys: arr(e.sharedKeys),
      weight: num(e.weight),
    })),
  };
}
const RENDER_EDGE_CAP = 600;

const retry = (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2);

type Kind = 'company' | 'scope' | 'tool' | 'sync' | 'sealed' | 'gnode';

interface DNode {
  id: string;
  kind: Kind;
  label: string;
  sub: string;
  status?: string;
  iconSlug?: string | null;
  domain?: string | null;
  expandable?: boolean;
  /** Set on reconstructed record nodes, so clicking one can open it. */
  entityId?: string | null;
  x: number;
  y: number;
  born: number;
}

const STATUS_COLOR: Record<string, string> = {
  EVIDENCE_BACKED: '#5da570',
  CANDIDATE: '#8a8069',
  CONTRADICTED: '#c07a3e',
  INSUFFICIENT: '#8a8069',
  SUPERSEDED: '#5c5647',
  COMPLETE: '#5da570',
  FAILED: '#b3593a',
  ACCESS_BLOCKED: '#b3593a',
  REGISTERED: '#8a8069',
  LISTING: '#c9a84c',
  DIFFING: '#c9a84c',
  PROBING: '#c9a84c',
};

const easeOutBack = (t: number) => 1 + 2.7 * Math.pow(t - 1, 3) + 1.7 * Math.pow(t - 1, 2);

/** How long a newly placed node takes to arrive. Then the canvas stops. */
const ENTRANCE_MS = 420;

/**
 * Cut a label to the column, with an ellipsis, measured rather than counted.
 *
 * Character counts were used before and they are wrong for a proportional
 * face: 22 characters of `WWWW` is twice the width of 22 of `illi`, so one
 * label overhung its column while another wasted half of it.
 */
function clip(ctx: CanvasRenderingContext2D, text: string, max: number): string {
  if (ctx.measureText(text).width <= max) return text;
  let low = 0;
  let high = text.length;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (ctx.measureText(`${text.slice(0, mid)}\u2026`).width <= max) low = mid;
    else high = mid - 1;
  }
  return `${text.slice(0, low)}\u2026`;
}

/**
 * Live discovery graph. Every node is a persisted backend fact; clicking an
 * expandable node fetches its REAL children from the API (sync runs, sealed
 * attribution toolset, published graph-projection nodes) and springs them into
 * the simulation. Expanded branches keep refreshing on the live poll.
 */
export function DiscoveryGraphLive() {
  const { bucketId } = useParams();
  /**
   * Whether anything on this page can still change on its own.
   *
   * Five reads here refreshed on fixed intervals with no condition at all -
   * two of them once per expanded node - so a bucket sealed days ago was
   * asked about roughly seventy-two times a minute for as long as the tab
   * stayed open. The walk stream is the only thing that knows whether there
   * is a job outstanding, and it already reports it; nothing was listening.
   */
  const [walkState, setWalkState] = useState<WalkState>('connecting');
  const walking = walkOutstanding(walkState);
  // This graph is one bucket's, and it used to find that bucket by fetching
  // every registered bucket in the account and scanning for its own id - then
  // fetching every company to print one name in a crumb that is gone. It asks
  // for the bucket it is drawing.
  const buckets = useQuery({
    queryKey: ['flow-bucket', bucketId],
    queryFn: ({ signal }) => backend.getBucket(bucketId!, signal),
    enabled: Boolean(bucketId),
    retry,
    // Gated, like every other read on this page. The bucket record changes
    // when a walk runs or a content-reader grant is registered; the second
    // invalidates this key on the write, so the poll is only for the first.
    refetchInterval: every(12_000, walking),
  });
  const scope = buckets.data;
  const companyId = scope?.bucket.companyId;
  const tools = useQuery({
    queryKey: ['company-tools', companyId],
    queryFn: ({ signal }) => backend.getCompanyTools(companyId!, signal),
    enabled: Boolean(companyId),
    retry,
    refetchInterval: every(6000, walking),
  });
  // A company-wide analysis run used to be polled here and named in the
  // banner below, on a graph that draws one bucket. Whether some other bucket
  // is being analysed is not this bucket's business.
  const scopeTools = (tools.data?.items ?? []).filter((t) => t.bucketId === bucketId);

  // The walk names each tool the moment it seals that tool's first object.
  // Holding those slugs lets the graph grow while the walk runs instead of
  // staying empty until the whole run finishes and the inventory catches up.
  const [liveTools, setLiveTools] = useState<string[]>([]);
  const onFrame = useCallback((f: { tools: string[] }) => {
    setLiveTools((prev) => (prev.length === f.tools.length && prev.every((t, i) => t === f.tools[i]) ? prev : f.tools));
  }, []);
  const evidenced = scopeTools.filter((t) => t.status === 'EVIDENCE_BACKED').length;
  const candidates = scopeTools.length - evidenced;
  const [expanded, setExpanded] = useState<string[]>([]);
  const [toolLens, setToolLens] = useState<string[]>([]);
  // The server's lenses are ALL, PERSONA, DOMAIN, TOOL and EVENT. CHANNELS was
  // offered as though it were one, then quietly rewritten to ALL and filtered
  // in the browser, so a client-side cut was presented as a server projection
  // while the real TOOL lens was never offered at all.
  const [lens, setLens] = useState<'ALL' | 'PERSONA' | 'DOMAIN' | 'TOOL' | 'EVENT'>('ALL');
  const [note, setNote] = useState<string | null>(null);
  const qc = useQueryClient();
  const onSealed = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['company-tools', companyId] });
    void qc.invalidateQueries({ queryKey: ['toolset', bucketId] });
    void qc.invalidateQueries({ queryKey: ['graph-projection', bucketId] });
    void qc.invalidateQueries({ queryKey: ['company-dashboard', companyId] });
  }, [qc, companyId, bucketId]);

  // Real children for every expanded scope, refreshed on the live cadence.
  const syncQ = useQueries({
    queries: expanded.map((id) => ({
      queryKey: ['sync-runs', id],
      queryFn: ({ signal }: { signal?: AbortSignal }) => backend.listSyncRuns(id, signal),
      retry,
      refetchInterval: every(6000, walking),
    })),
  });
  // A per-scope toolset poll every 12s that nothing read: its only remaining
  // reference was a stale dependency on the graph memo. Removing it also
  // removes one request per expanded scope, repeating for as long as the page
  // is open.
  const attribQ = useQueries({
    queries: expanded.map((id) => ({
      queryKey: ['attribution-objects', id],
      queryFn: ({ signal }: { signal?: AbortSignal }) => backend.listObjectAttributions(id, undefined, signal),
      retry,
      refetchInterval: every(15_000, walking),
    })),
  });
  const projQ = useQueries({
    queries: expanded.map((id) => ({
      queryKey: ['graph-projection', id, lens],
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        backend.getGraphProjection(id, lens, signal),
      retry,
      refetchInterval: every(12_000, walking),
    })),
  });

  // A canvas cannot draw an unbounded graph, so there is a bound - but it is
  // named, and what it leaves out is stated on screen rather than sliced away
  // in silence as 48 nodes and 96 edges used to be.
  const whole = useWholeGraphs(expanded, projQ.map((q) => q.data));

  // The backend groups every record shape into domains by the field names the
  // records share. That is a real graph - 134 shapes and 759 agreements on
  // capway - where the attribution star is one fact ("was found in") drawn
  // repeatedly. It leads when it has anything to say.
  const domainQ = useQuery({
    queryKey: ['domain-graph', bucketId],
    queryFn: ({ signal }) => backend.getDomainGraph(bucketId!, signal),
    enabled: Boolean(bucketId),
    retry,
    staleTime: 120_000,
  });
  const domain = useMemo(() => readDomainGraph(domainQ.data), [domainQ.data]);
  const [showDomain, setShowDomain] = useState(true);
  // Domains lead; a domain's shapes appear only when it is opened. Drawing all
  // 134 at once is a hairball nobody can read, and the structure that matters
  // at first glance is which domains exist and how big they are.
  const [openDomains, setOpenDomains] = useState<string[]>([]);
  const domainMode = showDomain && domain !== null && domain.nodes.length > 0;

  // A lens re-cuts PUBLISHED reconstruction. When no projection is READY
  // there is nothing to re-cut, and offering persona/domain/event as choices
  // invites an operator to click four buttons that each change nothing.
  const projection = projQ.find((q) => q.data)?.data as
    | { availability?: string; gapCode?: string }
    | undefined;
  const lensesLive = projection?.availability === 'AVAILABLE';

  const [openEntity, setOpenEntity] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const nodesRef = useRef<Map<string, DNode>>(new Map());
  const camRef = useRef<Camera>({ x: 0, y: 0, s: 1 });
  /** Set whenever anything that changes the picture changes. */
  const dirtyRef = useRef(true);
  const fittedRef = useRef<string | null>(null);
  const dragRef = useRef<{ x: number; y: number } | null>(null);
  /**
   * Whether the pointer travelled since it went down.
   *
   * A canvas gets `click` after a drag as well as after a tap, so panning the
   * view also expanded whatever node the drag began on. Found by dragging the
   * live graph: one pan opened a domain and took the count from 36 to 38.
   */
  const movedRef = useRef(false);
  const [scale, setScale] = useState(1);
  const imagesRef = useRef<Map<string, HTMLImageElement>>(new Map());

  const model = useMemo(() => {
    const now = performance.now();
    const map = nodesRef.current;
    const seen = new Set<string>();
    const edges: [string, string][] = [];
    const tethers: [string, string][] = [];
    const edgeMeta = new Map<string, { derivation: string; integrity: string }>();
    const upsert = (id: string, make: () => Omit<DNode, 'x' | 'y' | 'born'>, nearId?: string) => {
      seen.add(id);
      const prior = map.get(id);
      if (prior) {
        Object.assign(prior, make());
        return;
      }
      const near = nearId ? map.get(nearId) : undefined;
      const a = Math.random() * Math.PI * 2;
      map.set(id, {
        ...make(),
        x: (near?.x ?? 0) + Math.cos(a) * 46,
        y: (near?.y ?? 0) + Math.sin(a) * 46,
        born: now,
      } as DNode);
    };

    if (domainMode && domain) {
      // One node per domain at rest. Opening a domain adds its shapes beside
      // it, which the engine eases outward from where the domain sits, so the
      // expansion reads as unfolding rather than as a redraw.
      const shapeById = new Map(domain.nodes.map((n) => [n.signatureSha256, n]));
      const domainOfShape = new Map<string, string>();
      for (const g of domain.groups) {
        for (const sha of g.signatureSha256s) domainOfShape.set(sha, g.domainKey);
      }

      for (const g of domain.groups) {
        const id = `domain:${g.domainKey}`;
        const open = openDomains.includes(g.domainKey);
        upsert(id, () => ({
          id,
          kind: 'tool',
          label: `${g.objectCount.toLocaleString()} files`,
          sub: open
            ? `${g.shapeCount} shapes · open`
            : g.shapeCount > 1
              ? `${g.shapeCount} shapes · ${g.joiningKeys.slice(0, 3).join(' · ') || 'shared fields'}`
              : 'one shape on its own',
          iconSlug: g.toolIds[0] ?? null,
          status: g.toolIds.length > 0 ? 'EVIDENCE_BACKED' : 'CANDIDATE',
          expandable: g.shapeCount > 1,
        }));

        if (!open) continue;
        for (const sha of g.signatureSha256s) {
          const n = shapeById.get(sha);
          if (!n) continue;
          const sid = `shape:${sha}`;
          upsert(sid, () => ({
            id: sid,
            kind: 'gnode',
            label: `${n.objectCount.toLocaleString()} files`,
            sub: n.signatureKeys.slice(0, 3).join(' · ') || say(n.recordShape),
            iconSlug: n.toolId ?? null,
          }), id);
          edges.push([id, sid]);
        }
      }

      // Agreements. Between two open shapes they are drawn shape to shape;
      // otherwise they are lifted to the domains involved, so the top frame
      // still shows which domains share vocabulary rather than nothing at all.
      const domainPairs = new Set<string>();
      for (const e of domain.edges) {
        const from = `shape:${e.source}`;
        const to = `shape:${e.target}`;
        if (seen.has(from) && seen.has(to)) {
          edges.push([from, to]);
          edgeMeta.set(`${from}->${to}`, {
            derivation: 'DETERMINISTIC_JOIN',
            integrity: e.sharedKeys.length >= 3 ? 'VALID' : 'UNKNOWN_TARGET',
          });
          continue;
        }
        const a = domainOfShape.get(e.source);
        const b2 = domainOfShape.get(e.target);
        if (!a || !b2 || a === b2) continue;
        const key = a < b2 ? `${a}|${b2}` : `${b2}|${a}`;
        if (domainPairs.has(key)) continue;
        domainPairs.add(key);
        const da = `domain:${a}`;
        const db = `domain:${b2}`;
        if (!seen.has(da) || !seen.has(db)) continue;
        edges.push([da, db]);
        edgeMeta.set(`${da}->${db}`, { derivation: 'DETERMINISTIC_JOIN', integrity: 'VALID' });
      }

      for (const id of [...map.keys()]) if (!seen.has(id)) map.delete(id);
      return { edges, tethers, edgeMeta, count: seen.size };
    }

    if (scope) {
      const b = scope;
      const toolNodeByToolId = new Map<string, string>();
      upsert(b.bucket.id, () => ({ id: b.bucket.id, kind: 'scope', label: b.bucket.name, sub: say(b.bucket.inventoryCoverage), expandable: true }));
      for (const t of scopeTools) {
        toolNodeByToolId.set(t.toolId, t.id);
        upsert(
          t.id,
          () => ({
            id: t.id,
            kind: 'tool',
            label: t.display.label,
            sub:
              t.status === 'EVIDENCE_BACKED'
                ? `${t.evidence.signals.toLocaleString()} signals · ${t.evidence.families} families`
                : t.evidence.signals >= 10
                  ? `candidate · ${t.evidence.signals.toLocaleString()} signals · ${t.evidence.families}/${FAMILIES_REQUIRED} kinds of check`
                  : `candidate · ${t.evidence.signals}/10 signals`,
            status: t.status,
            iconSlug: t.display.iconSlug ?? t.toolId,
            domain: t.display.domain ?? null,
          }),
          t.bucketId,
        );
        edges.push([t.bucketId, t.id]);
      }

      // Tools the running walk has named but the published inventory has not
      // caught up on yet. They are drawn as provisional until a detection
      // exists, never merged into the confirmed set.
      const publishedIds = new Set(scopeTools.map((t) => t.toolId));
      for (const slug of liveTools) {
        if (publishedIds.has(slug)) continue;
        const id = `live:${slug}`;
        upsert(
          id,
          () => ({
            id,
            kind: 'tool',
            label: slug,
            sub: 'found in this walk · detection pending',
            status: 'CANDIDATE',
            iconSlug: slug,
            domain: null,
          }),
          b.bucket.id,
        );
        edges.push([b.bucket.id, id]);
      }

      expanded.forEach((scopeId, i) => {
        if (!seen.has(scopeId)) return;
        for (const r of syncQ[i]?.data?.items ?? []) {
          const id = `sync:${r.syncRunId}`;
          upsert(id, () => ({
            id,
            kind: 'sync',
            label: 'sync run',
            sub: `+${r.new} ~${r.changed} -${r.deleted}`,
            status: r.state,
          }), scopeId);
          edges.push([scopeId, id]);
        }
        // A second node per tool, repeating the same tool's file count, doubled
        // every label on screen and added no fact the tool node did not already
        // carry. Nine nodes became eighteen the moment anything was selected
        // and the graph read as broken rather than as expanded.
        const sealedObjects = attribQ[i]?.data?.items ?? [];
        for (const toolId of toolLens) {
          const toolNode = toolNodeByToolId.get(toolId);
          if (!toolNode) continue;
          const mine = sealedObjects.filter((o) => o.toolId === toolId).slice(0, 10);
          for (const o of mine) {
            const id = `obj:${o.objectKey}`;
            const tail = o.objectKey.split('/').pop() ?? o.objectKey;
            upsert(id, () => ({
              id,
              kind: 'gnode',
              label: tail.length > 22 ? `${tail.slice(0, 21)}…` : tail,
              sub: `sealed · ${o.provenance.toLowerCase()}`,
            }), toolNode);
            // The disposition row itself asserts object -> tool.
            edges.push([toolNode, id]);
          }
        }
        const proj = projQ[i]?.data;
        if (proj && proj.availability === 'AVAILABLE') {
          // Page one plus every page the cursor leads to. The projection has
          // always returned nextCursor and this page used to draw the first
          // page as though it were the graph.
          const all = [...proj.nodes, ...(whole[i]?.nodes ?? [])] as typeof proj.nodes;
          const eligible = all

            .filter((n) => (toolLens.length === 0 ? true : n.tool !== null && toolLens.includes(n.tool)));
          const wanted = eligible.slice(0, RENDER_NODE_CAP);
          for (const n of wanted) {
            const id = `g:${n.nodeId}`;
            upsert(id, () => ({ id, kind: 'gnode', label: n.label, sub: (n.class ?? n.nodeType).toLowerCase(), entityId: n.entityId ?? null }), n.tool ? toolNodeByToolId.get(n.tool) ?? scopeId : scopeId);
            // The record's own backend-asserted `tool` field ties it to its tool.
            const toolNode = n.tool ? toolNodeByToolId.get(n.tool) : undefined;
            if (toolNode) edges.push([toolNode, id]);
          }
          for (const e of ([...proj.edges, ...(whole[i]?.edges ?? [])] as typeof proj.edges).slice(0, RENDER_EDGE_CAP)) {
            if (seen.has(`g:${e.from}`) && seen.has(`g:${e.to}`)) {
              edges.push([`g:${e.from}`, `g:${e.to}`]);
              edgeMeta.set(`g:${e.from}->g:${e.to}`, { derivation: e.derivation, integrity: e.integrity });
            }
          }
          if (wanted.length > 0) tethers.push([scopeId, `g:${wanted[0]!.nodeId}`]);
        }
      });
    }
    for (const id of [...map.keys()]) if (!seen.has(id)) map.delete(id);
    return { edges, tethers, edgeMeta, count: seen.size };
  }, [scope, scopeTools, liveTools, expanded, toolLens, syncQ, projQ, attribQ, whole, domainMode, domain, openDomains]);

  /**
   * Where everything sits. Computed once per model, never per frame.
   *
   * The tethers are excluded deliberately. They existed to stop the spring
   * simulation flinging a detached branch off screen, which is a force-layout
   * problem and not a fact about the graph; feeding them to a tree layout
   * would invent parentage the backend never asserted.
   */
  const layout = useMemo(() => {
    // Bounded by what the canvas can actually show. A flat set of 36 domains
    // has no parent between any two of them, so without this it stacks into
    // one column taller than the viewport and fitting it means 15%.
    const placed = tidyForest([...nodesRef.current.keys()], model.edges, {
      ...TIDY,
      maxRows: bandRows(canvasRef.current?.clientHeight ?? 0),
    });
    for (const [id, at] of placed.at) {
      const node = nodesRef.current.get(id);
      if (node) {
        node.x = at.x;
        node.y = at.y;
      }
    }
    dirtyRef.current = true;
    return placed;
  }, [model]);

  /** Show all of it on arrival, and never again unasked. */
  const fitNow = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || nodesRef.current.size === 0) return;
    // A label runs to the RIGHT of its node, so the extent to fit is the last
    // column plus a label, not the last node. Fitting on centres alone clipped
    // every name in the rightmost column against the edge of the canvas.
    camRef.current = fitCamera(
      { ...layout.bounds, maxX: layout.bounds.maxX + LABEL_MAX + 40 },
      { width: canvas.clientWidth, height: canvas.clientHeight },
      FIT_PADDING,
    );
    setScale(camRef.current.s);
    dirtyRef.current = true;
  }, [layout]);

  /**
   * Fit on arrival, and again when this becomes a DIFFERENT graph.
   *
   * Not on every model change: re-fitting when a node is expanded is the
   * original defect, where opening something made everything smaller. But
   * switching between "what this data is" and "where it was found" replaces
   * every node on screen, and carrying the previous view's scale into it left
   * a 19-node tree drawn at the 15% that suited 36 domains.
   */
  const graphKind = `${domainMode}:${lens}`;
  useEffect(() => {
    if (nodesRef.current.size === 0) return;
    if (fittedRef.current === graphKind) return;
    fittedRef.current = graphKind;
    fitNow();
  }, [fitNow, layout, graphKind]);

  useEffect(() => {
    for (const n of nodesRef.current.values()) {
      const key = n.iconSlug ?? (n.domain ? `dom:${n.domain}` : null);
      if (!key || imagesRef.current.has(key)) continue;
      const icon = n.iconSlug ? bundledIcon(n.iconSlug) : null;
      const src = icon ? iconDataUri(icon, 64) : n.domain ? faviconServiceUrl(n.domain) : null;
      if (!src) continue;
      const img = new Image();
      img.src = src;
      imagesRef.current.set(key, img);
    }
  }, [model]);


  // Click: hit-test in world space; expandable nodes fetch their real children.
  const onClick = (ev: React.MouseEvent<HTMLCanvasElement>) => {
    // A pan ends in a click event. Without this, moving the view expanded
    // whatever happened to be under where the drag started.
    if (movedRef.current) {
      movedRef.current = false;
      return;
    }
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const world = screenToWorld(
      camRef.current,
      { x: ev.clientX - rect.left, y: ev.clientY - rect.top },
      { width: rect.width, height: rect.height },
    );
    for (const n of nodesRef.current.values()) {
      // The label is part of the target: a record's dot is 8px and asking
      // someone to hit that at 0.4 zoom is asking them to miss.
      const withinLabel = world.x > n.x && world.x < n.x + LABEL_MAX + 24 && Math.abs(n.y - world.y) < ROW_GAP / 2;
      if (Math.hypot(n.x - world.x, n.y - world.y) < 22 || withinLabel) {
        if (domainMode && n.id.startsWith('domain:')) {
          // Open or close this domain. The engine eases its shapes out from
          // where the domain already sits, so it unfolds rather than redraws.
          const key = n.id.slice('domain:'.length);
          const g = domain?.groups.find((x) => x.domainKey === key);
          const opening = !openDomains.includes(key);
          setOpenDomains((xs) => (opening ? [...xs, key] : xs.filter((x) => x !== key)));
          setNote(
            !opening || !g
              ? null
              : `${g.objectCount.toLocaleString()} files in ${g.shapeCount} shapes that agree${
                  g.joiningKeys.length > 0 ? ` on ${g.joiningKeys.slice(0, 8).join(', ')}` : ''
                }.${g.toolIds.length > 0 ? ` Attributed to ${g.toolIds.join(', ').replace(/_/g, ' ')}.` : ' No rule names it.'}`,
          );
          return;
        }
        if (n.kind === 'scope') {
          setNote(null);
          setExpanded((xs) => (xs.includes(n.id) ? xs.filter((x) => x !== n.id) : [...xs, n.id]));
        } else if (n.kind === 'tool') {
          const t = scopeTools.find((x) => x.id === n.id);
          if (t) {
            const selecting = !toolLens.includes(t.toolId);
            setExpanded((xs) => (xs.includes(t.bucketId) ? xs : [...xs, t.bucketId]));
            setToolLens((xs) => (selecting ? [...xs, t.toolId] : xs.filter((x) => x !== t.toolId)));
            const proj = projQ[expanded.indexOf(t.bucketId)]?.data;
            const hasRecords = proj?.availability === 'AVAILABLE' && proj.nodes.some((p) => p.tool === t.toolId);
            const sealedCount = (attribQ[expanded.indexOf(t.bucketId)]?.data?.items ?? []).filter((o) => o.toolId === t.toolId).length;
            setNote(
              !selecting
                ? null
                : hasRecords
                  ? `${n.label}: expanding its published records under the ${lens.toLowerCase()} lens.`
                  : sealedCount > 0
                    ? `${n.label}: showing its sealed source objects (deterministic attributions with evidence). Reconstructed records and relationships appear when entity publication runs.`
                    : `${n.label} selected. Its sealed objects load as the walk data arrives; reconstructed records follow entity publication.`,
            );
          }
        } else if (n.kind === 'gnode' && domainMode && n.id.startsWith('shape:')) {
          // In this view a node is a record shape, not a record. Reporting "no
          // published entity" about it would answer a question nobody asked.
          const sha = n.id.slice('shape:'.length);
          const shape = domain?.nodes.find((x) => x.signatureSha256 === sha);
          const peers = (domain?.edges ?? []).filter((e) => e.source === sha || e.target === sha);
          const vocabulary = [...new Set(peers.flatMap((e) => e.sharedKeys))];
          setNote(
            shape
              ? `${shape.objectCount.toLocaleString()} files carrying ${shape.signatureKeys.length} fields: ${shape.signatureKeys.slice(0, 10).join(', ')}${shape.signatureKeys.length > 10 ? '…' : ''}. ${
                  peers.length === 0
                    ? 'No other shape here shares its vocabulary.'
                    : `Agrees with ${peers.length} other ${peers.length === 1 ? 'shape' : 'shapes'} on ${vocabulary.slice(0, 8).join(', ')}.`
                }${shape.toolId ? ` Attributed to ${shape.toolId.replace(/_/g, ' ')}.` : ' No rule names it.'}`
              : null,
          );
        } else if (n.kind === 'gnode') {
          // A record node was inert. The preview route has always been able to
          // open one field by field, with the provenance of each.
          if (n.entityId) {
            setOpenEntity(n.entityId);
            setNote(null);
          } else {
            setNote(`${n.label} has no published entity behind it yet, so there is nothing to open.`);
          }
        } else if (n.kind === 'sync') {
          setNote(`Sync run ${String(n.status).toLowerCase()}: drift ${n.sub}. Full history lives on the scope page.`);
        }
        return;
      }
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    let raf = 0;
    const css = getComputedStyle(document.documentElement);
    const line = `rgb(${css.getPropertyValue('--line-2') || '60 55 44'})`;
    const inkSoft = `rgb(${css.getPropertyValue('--ink-3') || '138 128 105'})`;
    const inkMain = `rgb(${css.getPropertyValue('--ink') || '232 227 214'})`;
    const gold = '#C9A84C';

    const frame = () => {
      raf = requestAnimationFrame(frame);
      const nodes = [...nodesRef.current.values()];
      const now = performance.now();
      // A one-shot entrance, so an expansion is legible, and then stillness.
      // The idle breathing that used to run here jittered every node by
      // 1.6px forever, which is why nothing on this canvas was ever crisp.
      const settling = nodes.some((n) => now - n.born < ENTRANCE_MS);
      if (!dirtyRef.current && !settling) return;
      dirtyRef.current = settling;

      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
        canvas.width = w * dpr;
        canvas.height = h * dpr;
      }
      if (nodes.length === 0) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);
        return;
      }
      const idx = new Map(nodes.map((n, i) => [n.id, i]));
      const drawn = layout.treeEdges.filter(([a, b]) => idx.has(a) && idx.has(b));
      const crossing = layout.crossEdges.filter(([a, b]) => idx.has(a) && idx.has(b));
      const cam = camRef.current;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      ctx.translate(w / 2, h / 2);
      ctx.scale(cam.s, cam.s);
      ctx.translate(-cam.x, -cam.y);

      /**
       * The record-to-record edges, behind everything, as arcs.
       *
       * They are real and they are not parentage, so drawing them as straight
       * lines through the tree is what made the picture read as haphazard: two
       * different relations rendered identically. An arc that bows away from
       * the columns says "this is the other relation" without a legend.
       */
      ctx.lineWidth = 1;
      ctx.strokeStyle = line;
      ctx.globalAlpha = 0.28;
      for (const [aId, bId] of crossing) {
        const na = nodes[idx.get(aId)!]!;
        const nb = nodes[idx.get(bId)!]!;
        const bow = Math.min(Math.abs(nb.y - na.y) * 0.4 + 30, 200);
        ctx.beginPath();
        ctx.moveTo(na.x, na.y);
        ctx.quadraticCurveTo((na.x + nb.x) / 2 + bow, (na.y + nb.y) / 2, nb.x, nb.y);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;

      ctx.lineWidth = 1;
      for (const [aId, bId] of drawn) {
        const na = nodes[idx.get(aId)!]!, nb = nodes[idx.get(bId)!]!;
        const meta = model.edgeMeta.get(`${aId}->${bId}`);
        const t = Math.min((now - Math.max(na.born, nb.born)) / ENTRANCE_MS, 1);
        ctx.globalAlpha = t * 0.8;
        ctx.strokeStyle =
          meta?.integrity === 'BROKEN' || meta?.integrity === 'UNKNOWN_TARGET'
            ? '#b3593a'
            : meta?.integrity === 'CONTRADICTED'
              ? '#c07a3e'
              : line;
        ctx.setLineDash(meta?.derivation === 'DETERMINISTIC_JOIN' ? [5, 4] : []);
        // An elbow, not a diagonal. A tree read as columns needs its edges to
        // leave a parent horizontally and arrive at a child horizontally, so
        // the eye follows depth instead of resolving a fan of slopes; the
        // rounded corner is what keeps sixty of them from reading as a grid.
        const mid = na.x + (nb.x - na.x) * 0.55;
        const turn = Math.min(14, Math.abs(nb.y - na.y) / 2);
        ctx.beginPath();
        ctx.moveTo(na.x + 18, na.y);
        if (turn < 1) {
          ctx.lineTo(nb.x - 14, nb.y);
        } else {
          ctx.lineTo(mid - turn, na.y);
          ctx.quadraticCurveTo(mid, na.y, mid, na.y + Math.sign(nb.y - na.y) * turn);
          ctx.lineTo(mid, nb.y - Math.sign(nb.y - na.y) * turn);
          ctx.quadraticCurveTo(mid, nb.y, mid + turn, nb.y);
          ctx.lineTo(nb.x - 14, nb.y);
        }
        ctx.stroke();
      }
      ctx.setLineDash([]);

      for (const n of nodes) {
        const t = Math.min((now - n.born) / ENTRANCE_MS, 1);
        const k = t >= 1 ? 1 : easeOutBack(t);
        ctx.globalAlpha = Math.min(t * 1.6, 1);
        ctx.save();
        ctx.translate(n.x, n.y);
        ctx.scale(k, k);
        if (n.kind === 'company') {
          ctx.beginPath(); ctx.arc(0, 0, 17, 0, 7);
          ctx.strokeStyle = gold; ctx.lineWidth = 2; ctx.stroke();
          ctx.beginPath(); ctx.arc(0, 0, 5, 0, 7); ctx.fillStyle = gold; ctx.fill();
        } else if (n.kind === 'scope') {
          ctx.beginPath(); ctx.roundRect(-14, -9, 28, 18, 4);
          ctx.strokeStyle = expanded.includes(n.id) ? gold : inkSoft;
          ctx.lineWidth = 1.5; ctx.stroke();
          ctx.fillStyle = inkSoft;
          ctx.font = '600 10px "IBM Plex Sans", system-ui';
          ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.fillText(expanded.includes(n.id) ? '−' : '+', 0, 0.5);
        } else if (n.kind === 'sync') {
          ctx.beginPath(); ctx.arc(0, 0, 9, 0, 7);
          ctx.strokeStyle = STATUS_COLOR[n.status ?? ''] ?? inkSoft;
          ctx.lineWidth = 1.5; ctx.stroke();
          ctx.beginPath(); ctx.arc(0, 0, 3, 0, 7);
          ctx.fillStyle = STATUS_COLOR[n.status ?? ''] ?? inkSoft; ctx.fill();
        } else if (n.kind === 'gnode') {
          ctx.beginPath(); ctx.roundRect(-8, -8, 16, 16, 4);
          ctx.strokeStyle = inkSoft; ctx.lineWidth = 1.2; ctx.stroke();
        } else {
          const r = n.kind === 'sealed' ? 12 : 15;
          const candidate = n.kind === 'tool' && n.status !== 'EVIDENCE_BACKED';
          const selectedTool = n.kind === 'tool' && scopeTools.some((x) => x.id === n.id && toolLens.includes(x.toolId));
          if (candidate && !selectedTool) ctx.globalAlpha *= 0.55;
          ctx.beginPath(); ctx.arc(0, 0, r, 0, 7);
          ctx.fillStyle = 'rgba(255,255,255,0.06)'; ctx.fill();
          ctx.strokeStyle = selectedTool ? gold : STATUS_COLOR[n.status ?? ''] ?? inkSoft;
          ctx.lineWidth = 2;
          if (candidate) {
            // Dashed says "not yet evidence-backed". It used to march, which
            // meant the canvas could never stop redrawing to say a static fact.
            ctx.setLineDash([4, 3]);
          }
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.lineDashOffset = 0;
          const key = (n.iconSlug && bundledIcon(n.iconSlug)) ? n.iconSlug : n.domain ? `dom:${n.domain}` : n.iconSlug;
          const img = key ? imagesRef.current.get(key) : undefined;
          const ir = n.kind === 'sealed' ? 7 : 9;
          if (img?.complete && img.naturalWidth > 0) {
            ctx.drawImage(img, -ir, -ir, ir * 2, ir * 2);
          } else {
            ctx.fillStyle = inkMain;
            ctx.font = '600 11px "IBM Plex Sans", system-ui';
            ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.fillText(n.label[0]!.toUpperCase(), 0, 0.5);
          }
        }
        ctx.restore();

        const small = n.kind === 'sync' || n.kind === 'sealed' || n.kind === 'gnode';
        const labelFont = `${n.kind === 'company' ? 600 : 500} ${n.kind === 'company' ? 13 : small ? 10.5 : 11.5}px "IBM Plex Sans", system-ui`;
        const subFont = '10px "IBM Plex Mono", ui-monospace, monospace';
        /**
         * The label sits beside the node, left-aligned, clipped to one column.
         *
         * Centred underneath is what a force layout needs, because a node can
         * have a neighbour in any direction. In columns the only thing a label
         * can collide with is the row below it, so putting it to the right
         * turns the picture into an outline you read down, and no backing pill
         * is needed to rescue it from a line passing behind.
         */
        const nx = n.x + (small ? 14 : 22);
        const ly = n.y - (n.sub ? 11 : 5);
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        ctx.globalAlpha = Math.min(t * 1.6, 1);
        ctx.fillStyle = n.kind === 'company' ? inkMain : inkMain;
        ctx.font = labelFont;
        ctx.fillText(clip(ctx, n.label, LABEL_MAX), nx, ly);
        if (n.sub) {
          ctx.font = subFont;
          ctx.fillStyle = inkSoft;
          ctx.globalAlpha = Math.min(t, 0.75);
          ctx.fillText(clip(ctx, n.sub, LABEL_MAX), nx, ly + 13);
        }
      }
      ctx.globalAlpha = 1;
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [layout, model, expanded, toolLens, scopeTools]);

  /**
   * Wheel to zoom, drag to pan. Neither existed.
   *
   * Registered here rather than as a React prop because the wheel listener has
   * to be non-passive to call `preventDefault`, and React attaches wheel
   * handlers passively: without this the page scrolls behind the graph instead
   * of the graph zooming, which is the reported "I cannot even scroll".
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (ev: WheelEvent) => {
      ev.preventDefault();
      const rect = canvas.getBoundingClientRect();
      camRef.current = zoomAt(
        camRef.current,
        wheelFactor(ev.deltaY, ev.deltaMode),
        { x: ev.clientX - rect.left, y: ev.clientY - rect.top },
        { width: rect.width, height: rect.height },
      );
      setScale(camRef.current.s);
      dirtyRef.current = true;
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, []);

  useEffect(() => {
    const onResize = () => {
      dirtyRef.current = true;
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onPointerDown = (ev: React.PointerEvent<HTMLCanvasElement>) => {
    if (ev.button !== 0) return;
    dragRef.current = { x: ev.clientX, y: ev.clientY };
    movedRef.current = false;
  };
  const onPointerMove = (ev: React.PointerEvent<HTMLCanvasElement>) => {
    const from = dragRef.current;
    if (!from) return;
    const dx = ev.clientX - from.x;
    const dy = ev.clientY - from.y;
    if (Math.hypot(dx, dy) < 3) return;
    movedRef.current = true;
    ev.currentTarget.setPointerCapture(ev.pointerId);
    dragRef.current = { x: ev.clientX, y: ev.clientY };
    camRef.current = panBy(camRef.current, dx, dy);
    dirtyRef.current = true;
  };
  const endDrag = (ev: React.PointerEvent<HTMLCanvasElement>) => {
    if (ev.currentTarget.hasPointerCapture(ev.pointerId)) {
      ev.currentTarget.releasePointerCapture(ev.pointerId);
    }
    dragRef.current = null;
  };

  /** Zoom about the middle, for the buttons and the keyboard. */
  const zoomBy = useCallback((factor: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    camRef.current = zoomAt(camRef.current, factor, { x: rect.width / 2, y: rect.height / 2 }, {
      width: rect.width,
      height: rect.height,
    });
    setScale(camRef.current.s);
    dirtyRef.current = true;
  }, []);

  if (buckets.isPending) return <Loading what="Reading the graph for this bucket" rows={4} />;

  if (!scope)
    return (
      <EmptyState
        title="Scope unavailable"
        description={buckets.isError && buckets.error instanceof ApiError ? (buckets.error.reason ?? buckets.error.message) : 'This registered scope is not in your authorized catalog.'}
      />
    );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-none flex-wrap items-center gap-2 border-b border-line bg-panel px-4 py-2 text-small text-ink-3">
        {/* Two rungs of this trail led out of the bucket - to a catalogue of
            all of them, and to the company that owns several. The graph is
            this bucket's, so the trail is this bucket's. */}
        <Link to={`/buckets/${bucketId}`} className="mono hover:text-ink">{scope.bucket.name}</Link>
        <ChevronRight className="h-3 w-3" aria-hidden="true" />
        <span className="text-ink">Graph</span>
        {/* Two genuinely different graphs, so this is a choice rather than a
            filter: what the data is, or where it was found. */}
        {domain && domain.nodes.length > 0 && (
          <span className="ml-3 flex items-center gap-1">
            {([true, false] as const).map((on) => (
              <button
                key={String(on)}
                aria-pressed={showDomain === on}
                onClick={() => setShowDomain(on)}
                className={
                  'rounded px-2 py-0.5 text-caption transition-colors duration-fast ' +
                  (showDomain === on ? 'bg-acc-soft text-acc-2' : 'text-ink-3 hover:bg-hov hover:text-ink')
                }
              >
                {on ? `what this data is · ${domain.groups.length} domains` : 'where it was found'}
              </button>
            ))}
          </span>
        )}
        {lensesLive ? (
          <span className="ml-3 flex items-center gap-1">
            {(['ALL', 'PERSONA', 'DOMAIN', 'TOOL', 'EVENT'] as const).map((l) => (
              <button
                key={l}
                aria-pressed={lens === l}
                onClick={() => setLens(l)}
                className={
                  'rounded px-2 py-0.5 text-caption transition-colors duration-fast ' +
                  (lens === l ? 'bg-acc-soft text-acc-2' : 'text-ink-3 hover:bg-hov hover:text-ink')
                }
              >
                {l.toLowerCase()}
              </button>
            ))}
          </span>
        ) : domainMode ? null : (
          <span className="ml-3 text-caption text-ink-4" title={projection?.gapCode ?? 'NO_READY_GRAPH'}>
            persona, domain and event views re-cut reconstructed records; none are published yet
          </span>
        )}
        <span className="mono ml-auto flex items-center gap-3 text-caption">
          <span className="flex items-center gap-1.5">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-acc opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-acc" />
            </span>
            <Stamp tone="ok">LIVE</Stamp>
          </span>
          <span className="inline-block min-w-[70px]">{model.count} nodes</span>
          {/* fixed-width slot: fades, never reflows */}
          <span
            className={
              'inline-block w-[72px] text-acc-2 transition-opacity duration-fast ' +
              (tools.isFetching || buckets.isFetching ? 'opacity-100' : 'opacity-0')
            }
            aria-hidden={!(tools.isFetching || buckets.isFetching)}
          >
            updating…
          </span>
        </span>
      </div>
      <WalkProgress bucketId={bucketId!} onSealed={onSealed} onFrame={onFrame} onState={setWalkState} />
      {domainMode && domain && (
        <p className="border-b border-line bg-panel-2 px-4 py-2 text-caption text-ink-2">
          {domain.groups.length} domains over {domain.nodes.length.toLocaleString()} record shapes. A line is shared
          vocabulary, read out of the records rather than from a folder or a filename.{' '}
          {openDomains.length === 0
            ? 'Click a domain to unfold the shapes inside it.'
            : `${openDomains.length} open. Click again to fold it back.`}
        </p>
      )}
      {(() => {
        // What is drawn against what exists. This page used to slice to 48
        // nodes and ignore every page after the first, with nothing on screen
        // to say so - a truncated view of a large corpus was indistinguishable
        // from a small one.
        const fetched = whole.reduce((n, w) => n + w.nodes.length, 0);
        const nodeTotal =
          projQ.reduce((n, q) => n + (q.data?.availability === 'AVAILABLE' ? q.data.nodes.length : 0), 0) + fetched;
        const loading = whole.some((w) => w.loading);
        const stoppedEarly = whole.some((w) => w.stoppedEarly);
        const serverTruncated = whole.some((w) => w.serverTruncated);
        const drawn = Math.min(nodeTotal, RENDER_NODE_CAP);
        // Edges are capped by exactly the same kind of limit and were never
        // mentioned, so a graph could be missing most of its relationships
        // while the banner spoke only about records.
        const edgeTotal = projQ.reduce(
          (n, q) => n + (q.data?.availability === 'AVAILABLE' ? q.data.edges.length : 0),
          0,
        ) + whole.reduce((n, w) => n + w.edges.length, 0);
        const edgesCut = edgeTotal > RENDER_EDGE_CAP;
        // There was a fourth incompleteness here: a truncated page of the
        // account's bucket catalogue, which made this a partial graph of the
        // COMPANY however complete the per-bucket numbers were. This graph
        // draws one bucket, read directly, so that catalogue is not consulted
        // and cannot be short.
        if (!loading && !stoppedEarly && !serverTruncated && !edgesCut && nodeTotal <= RENDER_NODE_CAP)
          return null;
        return (
          <p className="border-b border-line bg-panel-2 px-4 py-2 text-caption text-ink-2">
            {loading
              ? `Reading the rest of the graph. ${fetched.toLocaleString()} more records so far.`
              : `Drawing ${drawn.toLocaleString()} of ${nodeTotal.toLocaleString()} records.`}
            {!loading && nodeTotal > RENDER_NODE_CAP && ' The rest are loaded and counted, just not drawn at once.'}
            {edgesCut &&
              ` Drawing ${RENDER_EDGE_CAP.toLocaleString()} of ${edgeTotal.toLocaleString()} connections.`}
            {stoppedEarly && ` Stopped after ${MAX_PAGES} pages; the backend still had more.`}
            {serverTruncated && ' The backend truncated its own page, so more exists than it returned.'}
          </p>
        );
      })()}
      <div className="relative min-h-0 flex-1 animate-pop">
        <canvas
          ref={canvasRef}
          className={`h-full w-full ${dragRef.current ? 'cursor-grabbing' : 'cursor-grab'}`}
          onClick={onClick}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          aria-hidden="true"
        />
        {/* The camera, as controls. There were none: the only way the view
            ever moved was an automatic re-fit inside the render loop, which
            is why opening a node made the whole graph smaller. */}
        <div className="absolute right-3 top-3 flex flex-col overflow-hidden rounded border border-line bg-panel">
          <button
            type="button"
            onClick={() => zoomBy(1.3)}
            title="Zoom in"
            aria-label="Zoom in"
            className="border-b border-line px-2 py-1 text-row text-ink-3 transition-colors duration-fast hover:bg-hov hover:text-ink"
          >
            +
          </button>
          <button
            type="button"
            onClick={() => zoomBy(1 / 1.3)}
            title="Zoom out"
            aria-label="Zoom out"
            className="border-b border-line px-2 py-1 text-row text-ink-3 transition-colors duration-fast hover:bg-hov hover:text-ink"
          >
            &minus;
          </button>
          <button
            type="button"
            onClick={fitNow}
            title="Fit the whole graph"
            aria-label="Fit the whole graph"
            className="px-2 py-1 text-caption text-ink-3 transition-colors duration-fast hover:bg-hov hover:text-ink"
          >
            fit
          </button>
        </div>
        <span className="mono pointer-events-none absolute right-3 top-[6.2rem] text-caption text-ink-4">
          {Math.round(scale * 100)}%
        </span>
        {openEntity && <EntityPreview entityId={openEntity} onClose={() => setOpenEntity(null)} />}
        <div className="mono pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded border border-line bg-panel px-3 py-1 text-caption text-ink-3">
          {domainMode
            ? 'a node is a domain until you open it · then its shapes · every line is shared vocabulary'
            : 'drag to move · scroll to zoom · left to right is depth · solid = source-explicit · dashed = deterministic join · a bowed line is a record referencing another record'}
        </div>
        <div className="pointer-events-none absolute left-1/2 top-4 flex max-w-2xl -translate-x-1/2 flex-col items-center gap-1 text-center">
          <div className="rounded border border-line bg-panel px-3 py-1.5 text-small text-ink-2">
            {evidenced > 0
                ? `${evidenced} evidence-backed tool${evidenced === 1 ? '' : 's'} · ${candidates} candidate${candidates === 1 ? '' : 's'} awaiting evidence.`
                : candidates > 0
                  ? `Discovery has found ${candidates} candidate tool${candidates === 1 ? '' : 's'}. They stay dashed until the evidence gate confirms them.`
                  : 'Tools appear here as discovery uncovers them.'}
          </div>
        </div>
        {note && (
          <div className="absolute left-1/2 top-4 max-w-lg -translate-x-1/2 rounded border border-line bg-panel px-3 py-1.5 text-small text-ink-2">
            {note}
          </div>
        )}
        <ul className="sr-only">
          {[...nodesRef.current.values()].map((n) => (
            <li key={n.id}>{`${n.kind}: ${n.label} (${n.sub}${n.status ? `, ${n.status}` : ''})`}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
